import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Calendar, Star, Trash2, Download, Upload, Search, Filter, Eye, Play, CheckCircle, Clock } from 'lucide-react'
import { useEventStore } from '../../../../stores/eventStore'
import { EventResult } from './EventResult'
import type { SavedEvent } from '@creanizer/types'

export function SavedEventsList() {
  const { 
    events, 
    removeEvent, 
    toggleFavorite, 
    markAsExecuted,
    addRating,
    exportAsText, 
    exportAsJSON,
    exportSingleAsText,
    exportSingleAsJSON,
    importFromJSON,
    clearAll
  } = useEventStore()
  
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterDifficulty, setFilterDifficulty] = useState<string>('all')
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false)
  const [showExecutedOnly, setShowExecutedOnly] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<SavedEvent | null>(null)
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list')
  
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      // テキスト検索
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        const matchesName = event.result.basicInfo.name.toLowerCase().includes(query)
        const matchesDescription = event.result.basicInfo.description?.toLowerCase().includes(query)
        const matchesSummary = event.result.basicInfo.summary?.toLowerCase().includes(query)
        const matchesType = event.result.basicInfo.type?.toLowerCase().includes(query)
        const matchesMemo = event.memo?.toLowerCase().includes(query)
        
        if (!matchesName && !matchesDescription && !matchesSummary && !matchesType && !matchesMemo) {
          return false
        }
      }
      
      // タイプフィルター
      if (filterType !== 'all' && event.result.basicInfo.type !== filterType) {
        return false
      }
      
      // 難易度フィルター
      if (filterDifficulty !== 'all') {
        const difficulty = event.result.metadata?.difficulty
        if (!difficulty) return false
        
        switch (filterDifficulty) {
          case 'easy': return difficulty <= 3
          case 'medium': return difficulty >= 4 && difficulty <= 7
          case 'hard': return difficulty >= 8
        }
      }
      
      // お気に入りフィルター
      if (showFavoritesOnly && !event.favorite) {
        return false
      }
      
      // 実行済みフィルター
      if (showExecutedOnly && !event.executed) {
        return false
      }
      
      return true
    })
  }, [events, searchQuery, filterType, filterDifficulty, showFavoritesOnly, showExecutedOnly])
  
  const handleImport = async () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        const text = await file.text()
        importFromJSON(text)
      }
    }
    input.click()
  }
  
  const handleRating = (event: SavedEvent, rating: number) => {
    addRating(event.id, rating)
  }
  
  if (viewMode === 'detail' && selectedEvent) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setViewMode('list')}>
            ← リストに戻る
          </Button>
        </div>
        <EventResult results={[selectedEvent.result]} mode="single" />
      </div>
    )
  }
  
  return (
    <div className="space-y-4">
      {/* フィルター・検索 */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="イベントを検索..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button 
                variant={showFavoritesOnly ? 'default' : 'outline'}
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              >
                <Star className="w-4 h-4" />
              </Button>
              <Button 
                variant={showExecutedOnly ? 'default' : 'outline'}
                onClick={() => setShowExecutedOnly(!showExecutedOnly)}
              >
                <CheckCircle className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="filter-type">イベントタイプ</Label>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger id="filter-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">すべて</SelectItem>
                    <SelectItem value="story">ストーリー</SelectItem>
                    <SelectItem value="encounter">遇遇</SelectItem>
                    <SelectItem value="exploration">探索</SelectItem>
                    <SelectItem value="social">社交</SelectItem>
                    <SelectItem value="puzzle">パズル</SelectItem>
                    <SelectItem value="battle">戦闘</SelectItem>
                    <SelectItem value="rest">休息</SelectItem>
                    <SelectItem value="discovery">発見</SelectItem>
                    <SelectItem value="crisis">危機</SelectItem>
                    <SelectItem value="celebration">祭り</SelectItem>
                    <SelectItem value="mystery">謎</SelectItem>
                    <SelectItem value="choice">選択</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="filter-difficulty">難易度</Label>
                <Select value={filterDifficulty} onValueChange={setFilterDifficulty}>
                  <SelectTrigger id="filter-difficulty">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">すべて</SelectItem>
                    <SelectItem value="easy">簡単 (1-3)</SelectItem>
                    <SelectItem value="medium">普通 (4-7)</SelectItem>
                    <SelectItem value="hard">困難 (8-10)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* アクションボタン */}
      <div className="flex gap-2 flex-wrap">
        <Button onClick={exportAsJSON} variant="outline" size="sm">
          <Download className="w-4 h-4 mr-2" />
          JSONエクスポート
        </Button>
        <Button onClick={exportAsText} variant="outline" size="sm">
          <Download className="w-4 h-4 mr-2" />
          テキストエクスポート
        </Button>
        <Button onClick={handleImport} variant="outline" size="sm">
          <Upload className="w-4 h-4 mr-2" />
          インポート
        </Button>
        <Button onClick={clearAll} variant="destructive" size="sm">
          <Trash2 className="w-4 h-4 mr-2" />
          すべて削除
        </Button>
      </div>
      
      {/* 結果表示 */}
      <div className="text-sm text-muted-foreground">
        {filteredEvents.length}件のイベントが見つかりました
      </div>
      
      {filteredEvents.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            {events.length === 0 
              ? 'イベントが保存されていません'
              : '条件に一致するイベントがありません'}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredEvents.map((event) => (
            <Card key={event.id} className="overflow-hidden">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {event.result.basicInfo.name}
                      {event.favorite && <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />}
                      {event.executed && <CheckCircle className="w-4 h-4 text-green-500" />}
                    </CardTitle>
                    <div className="flex flex-wrap gap-1">
                      <Badge>{event.result.basicInfo.type}</Badge>
                      <Badge variant="secondary">{event.result.basicInfo.category}</Badge>
                      {event.result.metadata?.difficulty && (
                        <Badge variant="outline">難易度: {event.result.metadata.difficulty}/10</Badge>
                      )}
                      {event.result.metadata?.playTime && (
                        <Badge variant="outline">
                          <Clock className="w-3 h-3 mr-1" />
                          {event.result.metadata.playTime}
                        </Badge>
                      )}
                      {event.result.scenario?.variations && (
                        <Badge variant="secondary">
                          <Badge className="w-3 h-3 mr-1" />
                          {event.result.scenario.variations.length}バリエーション
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => toggleFavorite(event.id)}
                    >
                      <Star className={`w-4 h-4 ${event.favorite ? 'fill-current' : ''}`} />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => removeEvent(event.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm">{event.result.basicInfo.summary}</p>
                
                {event.memo && (
                  <div className="text-sm bg-muted rounded p-2">
                    <strong>メモ:</strong> {event.memo}
                  </div>
                )}
                
                {event.executed && (
                  <div className="text-sm bg-green-50 dark:bg-green-950 rounded p-2">
                    <strong>実行済み:</strong> {new Date(event.executionDate!).toLocaleDateString('ja-JP')}
                    {event.executionNotes && <p className="text-xs mt-1">{event.executionNotes}</p>}
                  </div>
                )}
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">評価:</span>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => handleRating(event, star)}
                          className="p-1 hover:scale-110 transition-transform"
                        >
                          <Star 
                            className={`w-4 h-4 ${
                              event.rating && event.rating >= star 
                                ? 'fill-yellow-500 text-yellow-500' 
                                : 'text-muted-foreground'
                            }`} 
                          />
                        </button>
                      ))}
                    </div>
                    {event.usageCount && event.usageCount > 0 && (
                      <span className="text-xs text-muted-foreground">
                        ({event.usageCount}回使用)
                      </span>
                    )}
                  </div>
                  
                  {event.feedback && (
                    <p className="text-xs text-muted-foreground">{event.feedback}</p>
                  )}
                </div>
                
                <div className="flex gap-2 pt-2 border-t">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => {
                      setSelectedEvent(event)
                      setViewMode('detail')
                    }}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    詳細表示
                  </Button>
                  
                  {!event.executed && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => markAsExecuted(event.id)}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      実行済みにする
                    </Button>
                  )}
                  
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => exportSingleAsText(event.id)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    テキスト
                  </Button>
                  
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => exportSingleAsJSON(event.id)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    JSON
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}